import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

export default function Success() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentData, setPaymentData] = useState<any>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setError('No se encontró el ID de sesión');
      setLoading(false);
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const response = await fetch(`https://huellitas-unidas-server.vercel.app/api/verify-payment?session_id=${sessionId}`);
      const data = await response.json();

      if (response.ok) {
        setPaymentData(data);
        
        // Create user session from payment data
        if (data.customer_email) {
          // First, try to find the user in the database by email
          try {
            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('*')
              .eq('email', data.customer_email)
              .single();

            if (userData && !userError) {
              // User exists in database, use their data
              const userSession = {
                id: userData.id,
                email: userData.email,
                plan: userData.plan || data.plan || 'premium',
                expiresAt: Date.now() + (3 * 60 * 60 * 1000), // 3 hours
                created_at: userData.created_at,
                updated_at: userData.updated_at,
                shelter_name: userData.shelter_name,
                city: userData.city,
                state: userData.state,
                phone: userData.phone
              };
              
              localStorage.setItem('user_session', JSON.stringify(userSession));
            } else {
              // User doesn't exist in database, create temporary session
              const userSession = {
                id: data.customer_id || 'temp_id',
                email: data.customer_email,
                plan: data.plan || 'premium',
                expiresAt: Date.now() + (3 * 60 * 60 * 1000), // 3 hours
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              };
              
              localStorage.setItem('user_session', JSON.stringify(userSession));
            }
          } catch (dbError) {
            console.error('Error fetching user data:', dbError);
            // Fallback to temporary session
            const userSession = {
              id: data.customer_id || 'temp_id',
              email: data.customer_email,
              plan: data.plan || 'premium',
              expiresAt: Date.now() + (3 * 60 * 60 * 1000), // 3 hours
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            localStorage.setItem('user_session', JSON.stringify(userSession));
          }
        }
      } else {
        setError(data.error || 'Error al verificar el pago');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error al verificar el pago');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando tu pago...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4 text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Error en el Pago</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.href = '/register'}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Intentar Nuevamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4 text-center">
        <div className="text-green-500 text-6xl mb-4">✅</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">¡Pago Exitoso!</h1>
        
        {paymentData && (
          <div className="text-left mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Detalles del Pago:</h3>
              <p className="text-sm text-gray-600">
                <strong>Plan:</strong> {paymentData.plan || 'Premium'}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Email:</strong> {paymentData.customer_email}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Estado:</strong> {paymentData.status}
              </p>
              {paymentData.amount_total && (
                <p className="text-sm text-gray-600">
                  <strong>Monto:</strong> ${(paymentData.amount_total / 100).toFixed(2)} MXN
                </p>
              )}
            </div>
          </div>
        )}

        <p className="text-gray-600 mb-6">
          ¡Bienvenido a Huellitas Unidas! Tu suscripción premium está activa.
        </p>
        
        <div className="space-y-3">
          <button
            onClick={() => window.location.href = '/admin'}
            className="w-full bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Ir al Panel de Administración
          </button>
          
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Ir al Inicio
          </button>
        </div>
      </div>
    </div>
  );
}
